import { patch } from './patch';
import { pick } from 'lodash';
import up = require('updeep');
import { InviteAFriendForm } from '../../utils/forms/inviteAFriend';
import { Dispatch } from 'redux';
import * as selectors from '../selectors';
import initialState, { State } from '../state';
import { ExtraArguments } from '../extraArguments';
import { CompetitionPledges, getValidationErrors, someMatchingEmailDomain, validationErrorExists } from '../../utils/userApiHelper';
import { handleUserApiError } from './handleUnexpectedError';
import { toast } from './toast';
import { StyleState } from '../../utils/styles';
import { ValidationError } from '../../userApi';
import { trackEvent } from '../../utils/googleAnalytics';

export const removeInviteAFriendVE = (code: ValidationError.CodeEnum) => async (dispatch: Dispatch, getState: () => State, _extra: ExtraArguments) => {
  const { inviteAFriendVE } = getState();
  dispatch(patch({
    inviteAFriendVE: up(up.reject<ValidationError>((ve) => ve.code === code), inviteAFriendVE)
  }));
};

const inviteValidationToast = (
  dispatch: Dispatch,
  getState: () => State,
  validationErrors: ValidationError[],
  t: (key: string) => string
) => {
  if (validationErrorExists(ValidationError.CodeEnum.ErrInviteEmailDomainNotPermitted, validationErrors)) {
    toast({
      style: StyleState.Warning,
      message: t('You can only invite users with a valid competition email domain.')
    }, 5000)(dispatch, getState);
    return;
  }
  if (validationErrorExists(ValidationError.CodeEnum.ErrUserAlreadyInvited, validationErrors)) {
    toast({
      style: StyleState.Warning,
      message: t('This person has already been invited.')
    }, 5000)(dispatch, getState);
    return;
  }
  if (validationErrorExists(ValidationError.CodeEnum.ErrUserEmailExists, validationErrors)) {
    toast({
      style: StyleState.Warning,
      message: t('A user has already registered that email.')
    }, 5000)(dispatch, getState);
  }
};

export const postInvite = (inviteAFriendForm: InviteAFriendForm, formID: string) => async (dispatch: Dispatch, getState: () => State, extra: ExtraArguments) => {
  const t = extra.i18n.getFixedT(extra.i18n.language, 'inviteAFriend');
  const state = getState();
  const { competition } = state;
  const pledge = selectors.findPledgeByUUID(state, { uuid: CompetitionPledges.InviteAFriend });
  const invitePoints = pledge ? pledge.points : 25;
  dispatch(patch({ spinner: true }));
  try {
    const params = pick(inviteAFriendForm, ['name', 'email']);
    if (!someMatchingEmailDomain(params.email, competition.permittedDomains)) {
      const validationErrors = [{
        code: ValidationError.CodeEnum.ErrInviteEmailDomainNotPermitted,
        field: 'email',
        message: t('You can only invite users with a valid competition email domain.')
      }];
      dispatch(patch({
        inviteAFriendVE: validationErrors,
        spinner: false
      }));
      inviteValidationToast(dispatch, getState, validationErrors, t);
      return;
    }

    const api = selectors.userApi(state);
    await api.postInvite(state.competition.name, { name: params.name, email: params.email });
    trackEvent('invite', 'create');
    dispatch(patch({
      inviteAFriendVE: up.constant(initialState.inviteAFriendVE),
      savedInviteAFriendFormID: formID,
      spinner: false
    }));
    toast({
      style: StyleState.Success,
      message: t('Invite sent! You\'ll receive {{points}} points when they join the Challenge.', { points: invitePoints })
    }, 5000)(dispatch, getState);
  } catch (err) {
    const validationErrors = await getValidationErrors(err);
    if (validationErrors) {
      dispatch(patch({
        inviteAFriendVE: validationErrors,
        spinner: false
      }));
      inviteValidationToast(dispatch, getState, validationErrors, t);
      return;
    }
    handleUserApiError(err)(dispatch, getState);
    trackEvent('invite', 'unhandledError', 'create');
  }
};
